import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Activity, Zap, Wifi, WifiOff, Gauge, RefreshCw, Mail, Database, Home, Users, AtSign } from "lucide-react";
import api from "@/lib/api";


function ModeBadge({ mode }) {
  const map = {
    Live: { tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", icon: Wifi },
    Fallback: { tone: "text-amber-300 border-amber-500/40 bg-amber-500/10", icon: Activity },
    Offline: { tone: "text-rose-300 border-rose-500/40 bg-rose-500/10", icon: WifiOff },
  };
  const cfg = map[mode] || map.Offline;
  const Icon = cfg.icon;
  return (
    <span data-testid="enrichment-mode-badge" className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${cfg.tone}`}>
      <Icon className="h-3 w-3" /> {mode}
    </span>
  );
}


function Toggle({ label, enabled, onChange, testid, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      data-testid={testid}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border transition-colors text-xs ${
        enabled
          ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
          : "border-slate-700 text-slate-500"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-emerald-500/70"}`}
    >
      {enabled ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
      {label}: {enabled ? "Enabled" : "Disabled"}
    </button>
  );
}


function toneForPercent(pct) {
  if (pct >= 90) return { bar: "bg-rose-400", text: "text-rose-300", ring: "border-rose-500/40" };
  if (pct >= 70) return { bar: "bg-amber-400", text: "text-amber-300", ring: "border-amber-500/40" };
  return { bar: "bg-emerald-400", text: "text-emerald-300", ring: "border-emerald-500/40" };
}


function BalanceCard({ icon: Icon, title, testid, configured, children, footer, tone = "border-white/10" }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-sm border ${tone} bg-slate-950/30 p-3 space-y-2`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-slate-400" />
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-300">{title}</div>
        </div>
        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${configured ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-slate-500 border-slate-700 bg-slate-900/40"}`}>
          {configured ? "Configured" : "No Key"}
        </span>
      </div>
      {children}
      {footer && <div className="text-[10px] text-slate-500 pt-1 border-t border-white/5">{footer}</div>}
    </div>
  );
}


function ApiBalancesWidget() {
  const [bal, setBal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState(null);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/enrichment/balances");
      setBal(r.data);
      setLastError(null);
    } catch (e) {
      setLastError(e?.response?.data?.detail || "Failed to fetch balances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
    const id = setInterval(fetchBalances, 60000); // auto-refresh every 60s
    return () => clearInterval(id);
  }, []);

  if (!bal && !lastError) {
    return (
      <div className="ae-card p-4 text-slate-500 text-sm" data-testid="api-balances-widget-loading">
        <Gauge className="h-3.5 w-3.5 inline mr-2 animate-pulse" />
        Loading API balances…
      </div>
    );
  }

  if (lastError && !bal) {
    return (
      <div className="ae-card p-4 text-rose-300 text-sm border-rose-500/40" data-testid="api-balances-widget-error">
        {lastError}
        <button onClick={fetchBalances} className="ml-3 text-xs underline">Retry</button>
      </div>
    );
  }

  const brevo = bal.brevo || {};
  const bd = bal.batchdata || {};
  const attom = bal.attom || {};
  const pdl = bal.pdl || {};
  const hunter = bal.hunter || {};

  const brevoLow = brevo.status === "ok" && (brevo.email_credits ?? 0) < 50;
  const brevoOk = brevo.status === "ok";

  const bdPct = bd.percent_used || 0;
  const bdTone = toneForPercent(bdPct);
  const bdDailyPct = bd.daily_skip_trace_cap ? Math.round(((bd.daily_skip_trace_used || 0) / bd.daily_skip_trace_cap) * 100) : 0;

  const attomPct = attom.percent_used || 0;
  const attomTone = toneForPercent(attomPct);

  // PDL ,  free tier 100/mo, thresholds green<70, amber 70-90, red>90
  const pdlPct = pdl.percent_used || 0;
  const pdlTone = pdlPct > 90
    ? { bar: "bg-rose-400", text: "text-rose-300", ring: "border-rose-500/40" }
    : pdlPct >= 70
    ? { bar: "bg-amber-400", text: "text-amber-300", ring: "border-amber-500/40" }
    : { bar: "bg-emerald-400", text: "text-emerald-300", ring: "border-emerald-500/40" };

  // Hunter ,  free tier 50/mo, thresholds green<35, amber 35-45, red>45
  const hunterUsed = hunter.monthly_used ?? 0;
  const hunterTone = hunterUsed > 45
    ? { bar: "bg-rose-400", text: "text-rose-300", ring: "border-rose-500/40" }
    : hunterUsed >= 35
    ? { bar: "bg-amber-400", text: "text-amber-300", ring: "border-amber-500/40" }
    : { bar: "bg-emerald-400", text: "text-emerald-300", ring: "border-emerald-500/40" };
  const hunterPct = hunter.monthly_cap ? Math.round((hunterUsed / hunter.monthly_cap) * 100) : 0;

  return (
    <div className="ae-card p-4 space-y-3" data-testid="api-balances-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-emerald-400" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Live Monitor</div>
            <h3 className="font-heading text-sm text-slate-100">API Balances & Quotas</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 font-mono" data-testid="balances-fetched-at">
            {bal.fetched_at ? new Date(bal.fetched_at).toLocaleTimeString() : ""}
          </span>
          <button
            onClick={fetchBalances}
            disabled={loading}
            data-testid="balances-refresh-btn"
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-700 text-slate-400 hover:text-emerald-300 hover:border-emerald-500/40 disabled:opacity-50"
            title="Refresh (auto-refreshes every 60s)"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {/* Brevo */}
        <BalanceCard
          icon={Mail}
          title="Brevo (Email)"
          testid="balance-card-brevo"
          configured={brevo.configured}
          tone={brevoLow ? "border-rose-500/40" : brevoOk ? "border-emerald-500/20" : "border-white/10"}
          footer={<span>Source: Live · plan: <span className="font-mono">{brevo.plan_name || ", "}</span></span>}
        >
          {brevoOk ? (
            <div className="space-y-1">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Email credits</span>
                <span
                  data-testid="brevo-email-credits"
                  className={`font-mono text-lg font-semibold ${brevoLow ? "text-rose-300" : "text-slate-100"}`}
                >
                  {(brevo.email_credits ?? 0).toLocaleString()}
                </span>
              </div>
              {/* SMS credits hidden ,  SMS outreach disabled platform-wide */}
              {brevoLow && (
                <div className="text-[10px] text-rose-300 font-mono">⚠ Low balance ,  top-up recommended</div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-amber-300" data-testid="brevo-status-error">
              {brevo.status === "no_key" ? "API key not configured" : `Unavailable: ${brevo.detail || brevo.status}`}
            </div>
          )}
        </BalanceCard>

        {/* BatchData */}
        <BalanceCard
          icon={Database}
          title="BatchData"
          testid="balance-card-batchdata"
          configured={bd.configured}
          tone={bdTone.ring}
          footer={<span>Source: Internal counter · MTD</span>}
        >
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Monthly used</span>
              <span data-testid="batchdata-monthly-used" className={`font-mono text-sm font-semibold ${bdTone.text}`}>
                {(bd.monthly_used ?? 0).toLocaleString()} / {(bd.monthly_cap ?? 0).toLocaleString()}
              </span>
            </div>
            <div className="h-1.5 rounded-sm bg-slate-800 overflow-hidden">
              <div
                className={`h-full ${bdTone.bar} transition-all`}
                style={{ width: `${Math.min(bdPct, 100)}%` }}
                data-testid="batchdata-usage-bar"
              />
            </div>
            <div className="flex items-baseline justify-between pt-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Daily skip-traces</span>
              <span data-testid="batchdata-daily-used" className={`font-mono text-xs ${bdDailyPct >= 90 ? "text-rose-300" : "text-slate-200"}`}>
                {bd.daily_skip_trace_used ?? 0} / {bd.daily_skip_trace_cap ?? 0}
              </span>
            </div>
          </div>
        </BalanceCard>

        {/* ATTOM */}
        <BalanceCard
          icon={Home}
          title="ATTOM Data"
          testid="balance-card-attom"
          configured={attom.configured}
          tone={attomTone.ring}
          footer={<span>Source: Internal counter · MTD</span>}
        >
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Monthly used</span>
              <span data-testid="attom-monthly-used" className={`font-mono text-sm font-semibold ${attomTone.text}`}>
                {(attom.monthly_used ?? 0).toLocaleString()} / {(attom.monthly_cap ?? 0).toLocaleString()}
              </span>
            </div>
            <div className="h-1.5 rounded-sm bg-slate-800 overflow-hidden">
              <div
                className={`h-full ${attomTone.bar} transition-all`}
                style={{ width: `${Math.min(attomPct, 100)}%` }}
                data-testid="attom-usage-bar"
              />
            </div>
            <div className="flex items-baseline justify-between pt-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Remaining</span>
              <span data-testid="attom-monthly-remaining" className="font-mono text-xs text-slate-200">
                {(attom.monthly_remaining ?? 0).toLocaleString()}
              </span>
            </div>
          </div>
        </BalanceCard>

        {/* People Data Labs (PDL) */}
        <BalanceCard
          icon={Users}
          title="People Data Labs (PDL)"
          testid="balance-card-pdl"
          configured={pdl.configured}
          tone={pdl.byok_active ? "border-emerald-500/40" : pdlTone.ring}
          footer={<span>Source: Internal counter · MTD · {pdl.byok_active ? "subscriber key" : "free tier"}</span>}
        >
          {pdl.byok_active ? (
            <div className="text-[11px] text-emerald-300 font-mono py-2" data-testid="pdl-byok-active">
              ✓ BYOK ,  subscriber key active
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Monthly used</span>
                <span data-testid="pdl-monthly-used" className={`font-mono text-sm font-semibold ${pdlTone.text}`}>
                  {(pdl.monthly_used ?? 0).toLocaleString()} / {(pdl.monthly_cap ?? 0).toLocaleString()} calls
                </span>
              </div>
              <div className="h-1.5 rounded-sm bg-slate-800 overflow-hidden">
                <div
                  className={`h-full ${pdlTone.bar} transition-all`}
                  style={{ width: `${Math.min(pdlPct, 100)}%` }}
                  data-testid="pdl-usage-bar"
                />
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest pt-1">free tier</div>
            </div>
          )}
        </BalanceCard>

        {/* Hunter.io Email Finder */}
        <BalanceCard
          icon={AtSign}
          title="Hunter.io Email Finder"
          testid="balance-card-hunter"
          configured={hunter.configured}
          tone={hunter.byok_active ? "border-emerald-500/40" : hunterTone.ring}
          footer={<span>Source: Internal counter · MTD · {hunter.byok_active ? "subscriber key" : "free tier"}</span>}
        >
          {hunter.byok_active ? (
            <div className="text-[11px] text-emerald-300 font-mono py-2" data-testid="hunter-byok-active">
              ✓ BYOK ,  subscriber key active
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Monthly used</span>
                <span data-testid="hunter-monthly-used" className={`font-mono text-sm font-semibold ${hunterTone.text}`}>
                  {hunterUsed.toLocaleString()} / {(hunter.monthly_cap ?? 0).toLocaleString()} credits
                </span>
              </div>
              <div className="h-1.5 rounded-sm bg-slate-800 overflow-hidden">
                <div
                  className={`h-full ${hunterTone.bar} transition-all`}
                  style={{ width: `${Math.min(hunterPct, 100)}%` }}
                  data-testid="hunter-usage-bar"
                />
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest pt-1">free tier</div>
            </div>
          )}
        </BalanceCard>
      </div>
    </div>
  );
}


export default function EnrichmentSourcesPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [attomSource, setAttomSource] = useState("");
  const [attomBusy, setAttomBusy] = useState(false);

  const runAttomBatch = async () => {
    setAttomBusy(true);
    try {
      const payload = attomSource ? { discovery_source: attomSource } : {};
      const r = await api.post("/attom/stale-batch", payload);
      const d = r.data || {};
      toast.success(
        `ATTOM batch: enriched ${d.enriched ?? 0} • processed ${d.processed ?? 0}${attomSource ? ` • filter=${attomSource}` : ""}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ATTOM batch failed");
    } finally {
      setAttomBusy(false);
    }
  };

  const load = async () => {
    try {
      const r = await api.get("/settings/enrichment");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    setBusy(true);
    try {
      const r = await api.post("/settings/enrichment", { [key]: value });
      setData(r.data);
      toast.success(`${key.replace("_enabled", "")} → ${value ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const testAll = async () => {
    setTesting(true); setTestResults([]);
    try {
      const r = await api.post("/settings/enrichment/test-all");
      setTestResults(r.data.results || []);
      const passed = (r.data.results || []).filter((x) => x.status === "pass").length;
      toast.success(`Tested ${(r.data.results || []).length} sources: ${passed} passed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!data) return <div className="ae-card p-5 text-slate-500 text-sm">Loading…</div>;

  const usage = data.usage_today || {};
  const s = data.settings;

  return (
    <div className="space-y-4" data-testid="enrichment-sources-wrap">
      <ApiBalancesWidget />
      <div className="ae-card p-5 space-y-4" data-testid="enrichment-sources-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Admin</div>
          <h2 className="font-heading text-xl">Enrichment Sources</h2>
        </div>
        <ModeBadge mode={data.enrichment_mode} />
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {/* ATTOM */}
        <div className="border border-white/8 rounded-sm p-3 space-y-2" data-testid="src-attom">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-100">ATTOM Data</div>
            <Toggle label="ATTOM" enabled={s.attom_enabled} onChange={(v) => toggle("attom_enabled", v)} disabled={busy} testid="attom-toggle" />
          </div>
          <div className="text-[11px] text-slate-500">Calls today: <span className="text-slate-200 font-mono">{usage.attom_call_count || 0}</span></div>
          <div className="flex items-center gap-2 pt-1">
            <select
              data-testid="attom-batch-source"
              value={attomSource}
              onChange={(e) => setAttomSource(e.target.value)}
              className="text-[11px] bg-slate-900/60 border border-slate-800 rounded px-2 py-1 text-slate-200 flex-1"
            >
              <option value="">All stale</option>
              <option value="philadelphia_absentee_mf_sweep">Philadelphia sweep only</option>
            </select>
            <button
              onClick={runAttomBatch}
              disabled={attomBusy}
              data-testid="attom-batch-btn"
              className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 whitespace-nowrap"
            >
              {attomBusy ? "Enriching..." : "Run ATTOM Batch"}
            </button>
          </div>
        </div>

        {/* BatchData */}
        <div className="border border-white/8 rounded-sm p-3 space-y-2" data-testid="src-batchdata">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-100">BatchData</div>
            <Toggle label="BatchData" enabled={s.batchdata_enabled} onChange={(v) => toggle("batchdata_enabled", v)} disabled={busy} testid="batchdata-toggle" />
          </div>
          <div className="text-[11px] text-slate-500">
            Skip traces today: <span className={`font-mono ${usage.batchdata_skip_trace_count >= data.batchdata_daily_cap ? "text-rose-300" : "text-slate-200"}`}>
              {usage.batchdata_skip_trace_count || 0}/{data.batchdata_daily_cap}
            </span>
          </div>
          <div className="text-[11px] text-slate-500">Property lookups today: <span className="text-slate-200 font-mono">{usage.batchdata_property_lookup_count || 0}</span></div>
        </div>

        {/* Lead411 */}
        <div className="border border-white/8 rounded-sm p-3 space-y-2" data-testid="src-lead411">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-100">Lead411</div>
            <Toggle label="Lead411" enabled={s.lead411_enabled} onChange={(v) => toggle("lead411_enabled", v)} disabled={busy} testid="lead411-toggle" />
          </div>
          <div className="text-[11px] text-slate-500">Calls today: <span className="text-slate-200 font-mono">{usage.lead411_call_count || 0}</span></div>
        </div>
      </div>

      <div className="pt-2 border-t border-white/8 space-y-3">
        <button
          onClick={testAll} disabled={testing}
          data-testid="test-all-sources-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
        >
          <Zap className={`h-3.5 w-3.5 ${testing ? "animate-pulse" : ""}`} />
          {testing ? "Testing…" : "Test All Sources"}
        </button>
        {testResults.length > 0 && (
          <div className="grid md:grid-cols-3 gap-2 text-xs" data-testid="test-results">
            {testResults.map((r, i) => (
              <div key={i} className={`border rounded-sm p-2 ${
                r.status === "pass" ? "border-emerald-500/40 bg-emerald-500/5"
                  : r.status === "fail" ? "border-rose-500/40 bg-rose-500/5"
                  : "border-slate-700 bg-slate-800/30"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-slate-200 font-semibold">{r.source}</span>
                  <span className={`font-mono uppercase text-[10px] ${
                    r.status === "pass" ? "text-emerald-300"
                      : r.status === "fail" ? "text-rose-300" : "text-slate-500"}`}>
                    {r.status}
                  </span>
                </div>
                {r.ms > 0 && <div className="text-slate-500 text-[10px] mt-0.5">{r.ms}ms</div>}
                {r.detail && r.detail !== "ok" && <div className="text-slate-500 text-[10px] truncate" title={r.detail}>{r.detail}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
